import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface CustomSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  iconSvg?: string;
}

@Component({
  selector: 'app-custom-select',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './custom-select.component.html',
  styleUrl: './custom-select.component.scss',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CustomSelectComponent),
    multi: true
  }]
})
export class CustomSelectComponent implements ControlValueAccessor {
  @Input() options: CustomSelectOption[] = [];
  @Input() placeholder = 'Select';
  @Input() searchable = false;
  @Input() multiple = false;
  @Input() set disabled(val: boolean) { this._disabled = val; }
  get disabled(): boolean { return this._disabled; }

  isOpen = false;
  // true quando sotto il trigger non c'è spazio per il menu: si apre verso l'alto
  openUpward = false;
  searchTerm = '';
  private _disabled = false;
  value: string | string[] | null = null;
  private iconCache = new Map<string, SafeHtml>();

  private onChange: (value: string | string[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private sanitizer: DomSanitizer, private host: ElementRef<HTMLElement>) {}

  get filteredOptions(): CustomSelectOption[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term.length) {
      return this.options;
    }
    return this.options.filter(option => option.label.toLowerCase().includes(term));
  }

  get displayLabel(): string {
    if (!this.value) {
      return this.placeholder;
    }
    if (this.multiple && Array.isArray(this.value)) {
      if (this.value.length === 0) {
        return this.placeholder;
      }
      if (this.value.length === 1) {
        const firstValue = this.value[0];
        return this.options.find(option => option.value === firstValue)?.label || firstValue;
      }
      return `${this.value.length} selected`;
    }
    return this.options.find(option => option.value === this.value)?.label || (this.value as string);
  }

  get selectedOption(): CustomSelectOption | null {
    if (this.multiple) {
      return null;
    }
    const value = typeof this.value === 'string' ? this.value : null;
    if (!value) {
      return null;
    }
    return this.options.find(option => option.value === value) ?? null;
  }

  get selectedIcon(): SafeHtml | null {
    const option = this.selectedOption;
    if (!option) {
      return null;
    }
    return this.getOptionIcon(option);
  }

  isSelected(optionValue: string): boolean {
    if (this.multiple && Array.isArray(this.value)) {
      return this.value.includes(optionValue);
    }
    return this.value === optionValue;
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.openUpward = this.shouldOpenUpward();
    } else {
      this.resetSearch();
      this.onTouched();
    }
  }

  // Spazio visibile sotto e sopra il trigger, limitato dalla finestra e dal primo antenato
  // che taglia il contenuto (es. il corpo scrollabile di una modale).
  private shouldOpenUpward(): boolean {
    const rect = this.host.nativeElement.getBoundingClientRect();
    let top = 0;
    let bottom = window.innerHeight;
    for (let el = this.host.nativeElement.parentElement; el; el = el.parentElement) {
      const style = getComputedStyle(el);
      if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
        const clip = el.getBoundingClientRect();
        top = Math.max(top, clip.top);
        bottom = Math.min(bottom, clip.bottom);
        break;
      }
    }
    const spaceBelow = bottom - rect.bottom;
    const spaceAbove = rect.top - top;
    return spaceBelow < CustomSelectComponent.DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
  }

  // Deve restare allineato a max-height di .dropdown-select__dropdown (+ il margine di 4px)
  private static readonly DROPDOWN_MAX_HEIGHT = 244;

  onSearch(term: string): void {
    this.searchTerm = term;
  }

  selectOption(option: CustomSelectOption, event?: Event): void {
    event?.stopPropagation();
    if (this.disabled || option.disabled) {
      return;
    }

    if (this.multiple) {
      const currentValues = Array.isArray(this.value) ? [...this.value] : [];
      const index = currentValues.indexOf(option.value);

      if (index > -1) {
        // Remove if already selected
        currentValues.splice(index, 1);
      } else {
        // Add if not selected
        currentValues.push(option.value);
      }

      this.value = currentValues;
      this.onChange(this.value);
      // Don't close dropdown for multiple selection
    } else {
      this.value = option.value;
      this.onChange(this.value);
      this.onTouched();
      this.closeDropdown();
    }
  }

  getOptionIcon(option: CustomSelectOption): SafeHtml | null {
    if (!option.iconSvg) {
      return null;
    }
    const cacheKey = `${option.value}:${option.iconSvg}`;
    if (this.iconCache.has(cacheKey)) {
      return this.iconCache.get(cacheKey)!;
    }
    const safe = this.sanitizer.bypassSecurityTrustHtml(option.iconSvg);
    this.iconCache.set(cacheKey, safe);
    return safe;
  }

  closeDropdown(): void {
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;
    this.resetSearch();
  }

  @HostListener('document:click')
  handleDocumentClick(): void {
    this.closeDropdown();
  }

  writeValue(value: string | string[] | null): void {
    if (this.multiple) {
      this.value = Array.isArray(value) ? value : [];
    } else {
      this.value = value ?? null;
    }
  }

  registerOnChange(fn: (value: string | string[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private resetSearch(): void {
    this.searchTerm = '';
  }
}
